<template>
    <DataTable
        :header="$t('availabilityInTimeAccordingToStores')"
        :fields="fields"
        :items="items"
        :loading="loading"
        show-export-button
        class="mb-4"
    >
        <template #headerSide>
            <div class="d-flex gap-3 ml-auto ml-auto">
                <div class="d-flex align-items-center gap-1">
                    <div class="d-inline-block width-50 height-20" :style="getStyle('green')"></div> {{ $t('currentlyAvailable') }}
                </div>
                <div class="d-flex align-items-center gap-1">
                    <div class="d-inline-block width-50 height-20" :style="getStyle('red')"></div> {{ $t('currentlyUnavailable') }}
                </div>
            </div>
        </template>
        <template v-for="campaign in campaigns" #[`cell(campaign-${campaign.id})`]="{item, value}">
            <div :key="campaign.id" class="text-center">
                <div
                    class="nowrap px-2"
                    :style="getStyle(item.isAvailable[campaign.id] ? 'green' : 'red')"
                    :title="item.isAvailable[campaign.id] ? $t('currentlyAvailable') : $t('currentlyUnavailable')"
                >
                    {{ value }}
                </div>
            </div>
        </template>
        <template #cell(category)="{item, value}">
            <div v-if="item.category" :title="item.categories.map(item => item.name).join(' > ')">{{ value }}</div>
        </template>
    </DataTable>
</template>



<script>
    import DataTable from '@/components/common/DataTable.vue';

    export default {
        name: 'AvailabilityInTimeAccordingToStores',
        components: {
            DataTable,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                request: this.parentComponent.requestData,
                requestCampaigns: this.$req('buyboxes/AvailableCampaigns'),
                colorStyle: {
                    green: {
                        backgroundColor: 'rgba(0,255,0,0.1)',
                        color: 'green',
                    },
                    red: {
                        backgroundColor: 'rgba(255,0,0,0.1)',
                        color: 'red',
                    },
                },
                data: {},
                items: [],
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            loading() {
                return this.request.getLoading() || this.requestCampaigns.getLoading() || this.parentComponent.waitingLoading;
            },
            requestError() {
                return this.request.getError();
            },
            campaigns() {
                return this.requestCampaigns.getData() || [];
            },
            selectedCampaigns() {
                return this.parentComponent.campaigns || [];
            },
            selectedStoreCampaigns() {
                if (this.selectedCampaigns.length > 0) {
                    return this.campaigns.filter(campaign => this.selectedCampaigns.includes(campaign.id));
                }
                return structuredClone(this.campaigns);
            },
            timeRange() {
                return this.parentComponent.timeRange;
            },
            reportDateRangeInDays() {
                const { startDate, endDate } = this.timeRange || {};
                if (startDate && endDate) {
                    const startDateTime = new Date(startDate).valueOf();
                    const endDateTime = new Date(endDate).valueOf();
                    return Math.floor((endDateTime - startDateTime) / (1000 * 60 * 60 * 24));
                }
                return 0;
            },
            requestData() {
                return this.request.getData() || {};
            },
            dynamicStoreKeys() {
                const keys = new Set();
                Object.values(this.data).forEach(productObj => {
                    const charts = productObj.charts || {};
                    Object.keys(charts).forEach(chartKey => {
                        charts[chartKey].forEach(chartObj => {
                            if (chartObj.offer && chartObj.offer.campaign) {
                                keys.add(chartObj.offer.campaign);
                            }
                        });
                    });
                });
                return Array.from(keys).sort();
            },
            fields() {
                const staticFields = [
                    { key: 'category', label: this.$t('category'), sortable: true, exportable: true },
                    { key: 'subcategory1', label: this.$t('subcategory1'), sortable: true, exportable: true, show: false },
                    { key: 'subcategory2', label: this.$t('subcategory2'), sortable: true, exportable: true, show: false },
                    { key: 'subcategory3', label: this.$t('subcategory3'), sortable: true, exportable: true, show: false },
                    { key: 'subcategory4', label: this.$t('subcategory4'), sortable: true, exportable: true, show: false },
                    { key: 'name', label: this.$t('product'), sortable: true, exportable: true },
                    { key: 'sku', label: this.$t('model'), sortable: true, exportable: true },
                    { key: 'ean', label: 'EAN', sortable: true, exportable: true },
                ];
                const dynamicFields = this.selectedStoreCampaigns.map(campaign => ({
                    key: `campaign-${campaign.id}`,
                    label: campaign.name,
                    sortable: true,
                    exportable: true,
                    exportFormatter: val => {
                        const vals = val.split('/').map(item => Number.parseInt(item.trim()));
                        const diff = this.$format.percent((vals[0] * 100) / vals[1]);
                        return `${val} (${diff})`;
                    },
                }));
                return [...staticFields, ...dynamicFields];
            },
        },
        watch: {
            requestData(newVal, oldVal) {
                if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
                    this.data = newVal;
                }
            },
            requestError(error) {
                if (error) {
                    this.data = {};
                }
            },
            data(data) {
                this.initItems();
            },
        },
        methods: {
            initItems() {
                const items = [];
                const dateRangeInDays = this.reportDateRangeInDays;
                Object.values(this.data).forEach(productObj => {
                    const product = productObj.product || {};
                    let category = '';
                    let subcategory1 = '';
                    let subcategory2 = '';
                    let subcategory3 = '';
                    let subcategory4 = '';
                    if (product.categories && product.categories.length > 0 && product.categories[0].length > 0) {
                        const categories = [...product.categories[0]].reverse();
                        category = categories[0]?.name || '';
                        subcategory1 = categories[1]?.name || '';
                        subcategory2 = categories[2]?.name || '';
                        subcategory3 = categories[3]?.name || '';
                        subcategory4 = categories[4]?.name || '';
                    }
                    const name = product.name || '';
                    const sku = product.sku || '';
                    const ean = product.ean || '';
                    const row = {
                        productId: product.id,
                        category,
                        subcategory1,
                        subcategory2,
                        subcategory3,
                        subcategory4,
                        name,
                        sku,
                        ean,
                        campaignIds: {},
                        isAvailable: {},
                        categories: product.categories?.[0] || [],
                    };
                    this.selectedStoreCampaigns.forEach(campaign => {
                        const key = `campaign-${campaign.id}`;
                        const availabilityInDays = this.computeAvailabilityForProductAndStore(productObj, campaign.name);
                        row[key] = dateRangeInDays === 0 ? 'N/A' : `${availabilityInDays} / ${dateRangeInDays}`;
                        row.campaignIds[campaign.id] = this.getCampaignIdForStore(productObj, campaign.name);
                        row.isAvailable[campaign.id] = availabilityInDays > 0;
                    });
                    items.push(row);
                });
                this.items = items;
            },
            fetchCampaigns() {
                const id = this.$store.getters['plugin/getProducerPlugin'].id;
                return this.requestCampaigns.get({ urlParams: { id } });
            },
            /* eslint-disable no-restricted-syntax, guard-for-in */
            getCampaignIdForStore(productObj, store) {
                const charts = productObj.charts || {};
                for (const chartKey in charts) {
                    const offers = charts[chartKey];
                    for (let i = 0; i < offers.length; i++) {
                        const offer = offers[i].offer;
                        if (offer && offer.campaign === store) {
                            return offer.campaignId;
                        }
                    }
                }
                return null;
                /* eslint-enable no-restricted-syntax, guard-for-in */
            },
            mergeSegments(segments) {
                if (!segments || segments.length === 0) return [];
                const sorted = segments.slice().sort((a, b) => a.start - b.start);
                const merged = [];
                let current = sorted[0];
                for (let i = 1; i < sorted.length; i++) {
                    const seg = sorted[i];
                    if (seg.start <= current.end) {
                        current.end = Math.max(current.end, seg.end);
                    } else {
                        merged.push(current);
                        current = seg;
                    }
                }
                merged.push(current);
                return merged;
            },
            limitSegments(segments, reportStart, reportEnd) {
                return segments
                    .map(seg => {
                        const start = Math.max(seg.start, reportStart);
                        const end = Math.min(seg.end, reportEnd);
                        return { start, end };
                    })
                    .filter(seg => seg.end > seg.start);
            },
            computeAvailabilityForProductAndStore(productObj, store) {
                const allSegments = [];
                const charts = productObj.charts || {};
                Object.keys(charts).forEach(chartKey => {
                    charts[chartKey].forEach(chartObj => {
                        const offer = chartObj.offer;
                        if (offer && offer.campaign === store) {
                            const segments = chartObj.chart.segments || [];
                            segments.forEach(segment => {
                                allSegments.push({ start: segment.start, end: segment.end });
                            });
                        }
                    });
                });
                if (allSegments.length === 0) return 0;
                const { startDate, endDate } = this.timeRange || {};
                let reportStart = -Infinity;
                let reportEnd = Infinity;
                if (startDate && endDate) {
                    reportStart = new Date(startDate).valueOf();
                    reportEnd = new Date(endDate).valueOf();
                }
                const merged = this.mergeSegments(allSegments);
                const limited = this.limitSegments(merged, reportStart, reportEnd);
                const totalMs = limited.reduce((sum, seg) => sum + (seg.end - seg.start), 0);
                return Math.round(totalMs / (1000 * 60 * 60 * 24));
            },
            isAvailable(productId, campaignId) {
                const row = this.items.find(item => item.productId === productId);
                if (!row || !row.campaignIds) return null;
                const store = Object.keys(row.campaignIds).find(key => row.campaignIds[key] === campaignId);
                if (!store) return null;
                const [availabilityInDaysStr] = row[store].split(' / ');
                const availabilityInDays = parseInt(availabilityInDaysStr, 10);
                return availabilityInDays > 0;
            },
            getStyle(type) {
                return this.colorStyle[type] || {};
            },
        },
        created() {
            this.fetchCampaigns();
        },
    };
</script>
